(function () {
  if (window.NaxosStickyPlayer) {
    return;
  }

  const clipDuration = 30;
  const storageKey = "naxosPersistentPreviewPlayer";
  const player = document.querySelector("[data-naxos-sticky-player]");
  const root = document.documentElement;

  if (!player || typeof window.Audio !== "function") {
    return;
  }

  const audio = new window.Audio();
  const contextElement = player.querySelector("[data-naxos-player-context]");
  const titleElement = player.querySelector("[data-naxos-player-title]");
  const stateElement = player.querySelector("[data-naxos-player-state]");
  const previousButton = player.querySelector("[data-naxos-player-previous]");
  const toggleButton = player.querySelector("[data-naxos-player-toggle]");
  const nextButton = player.querySelector("[data-naxos-player-next]");
  const closeButton = player.querySelector("[data-naxos-player-close]");
  const currentTimeElement = player.querySelector("[data-naxos-player-current-time]");
  const durationElement = player.querySelector("[data-naxos-player-duration]");
  const progressInput = player.querySelector("[data-naxos-player-progress]");
  const statusElement = player.querySelector("[data-naxos-player-status]");

  let queue = [];
  let activeIndex = -1;
  let displayTime = 0;
  let resumeTime = 0;
  let clipEndHandled = false;
  let resettingAudio = false;
  let playbackErrorHandled = false;
  let lastSaveSecond = -1;

  audio.preload = "metadata";

  function getPreviewUrl(preview) {
    return preview?.dataset.naxosPreviewUrl || preview?.dataset.phononetPreviewUrl || "";
  }

  function getPreviewTitle(preview) {
    return preview?.dataset.naxosPreviewTitle || preview?.dataset.phononetPreviewTitle || window.NaxosText("naxos_ui.preview");
  }

  function getTrackFromPreview(preview) {
    const product = preview?.closest("[data-naxos-product-page]");
    return {
      albumTitle: preview?.dataset.naxosPreviewAlbumTitle || product?.dataset.title || "",
      artist: preview?.dataset.naxosPreviewArtist || product?.dataset.artist || "",
      composer: preview?.dataset.naxosPreviewComposer || product?.dataset.composer || "",
      id: preview?.dataset.naxosFavoriteId || "",
      imageUrl: preview?.dataset.naxosPreviewImageUrl || product?.dataset.imageUrl || "",
      number: preview?.dataset.naxosPreviewNumber || "",
      productUrl: preview?.dataset.naxosPreviewProductUrl || product?.dataset.url || "",
      title: getPreviewTitle(preview),
      url: getPreviewUrl(preview),
    };
  }

  function tracksMatch(firstTrack, secondTrack) {
    if (!firstTrack || !secondTrack) {
      return false;
    }

    if (firstTrack.id && secondTrack.id) {
      return firstTrack.id === secondTrack.id;
    }

    return firstTrack.url === secondTrack.url && firstTrack.title === secondTrack.title;
  }

  function getCurrentItem() {
    return queue[activeIndex] || null;
  }

  function formatTime(totalSeconds) {
    const safeSeconds = Math.max(0, Math.floor(Number.isFinite(totalSeconds) ? totalSeconds : 0));
    const minutes = Math.floor(safeSeconds / 60);
    const seconds = safeSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
  }

  function announce(message) {
    if (!statusElement) {
      return;
    }

    statusElement.textContent = "";
    window.requestAnimationFrame(() => {
      statusElement.textContent = message;
    });
  }

  function setState(message) {
    if (stateElement) {
      stateElement.textContent = message;
    }
  }

  function isRestrictedPlaybackPath(pathname) {
    return /(^|\/)(cart|checkout|checkouts|account|challenge)(\/|$)/i.test(pathname);
  }

  function saveState() {
    if (player.hidden || !queue.length || activeIndex < 0) {
      return;
    }

    const currentTime = audio.hasAttribute("src") ? audio.currentTime || 0 : displayTime;
    const snapshot = {
      activeIndex,
      currentTime: Math.min(clipDuration, Math.max(0, currentTime)),
      queue,
      visible: true,
    };

    try {
      window.sessionStorage.setItem(storageKey, JSON.stringify(snapshot));
    } catch (_error) {
      // Playback remains available when storage is blocked.
    }
  }

  function clearSavedState() {
    try {
      window.sessionStorage.removeItem(storageKey);
    } catch (_error) {
      // Nothing else is required when storage is blocked.
    }
  }

  function collectQueue(button) {
    const section = button.closest("[data-naxos-preview-section]");
    const previews = Array.from(section?.querySelectorAll("[data-naxos-preview-item]") || []);

    return previews
      .map(getTrackFromPreview)
      .filter((item) => item.url);
  }

  function findButtonIndex(button, nextQueue) {
    const preview = button.closest("[data-naxos-preview-item]");
    const clickedTrack = getTrackFromPreview(preview);
    return nextQueue.findIndex((item) => tracksMatch(item, clickedTrack));
  }

  function queuesMatch(firstQueue, secondQueue) {
    return (
      firstQueue.length === secondQueue.length &&
      firstQueue.every((item, index) => tracksMatch(item, secondQueue[index]))
    );
  }

  function updateButtonLabel(button, isPlaying) {
    const preview = button.closest("[data-naxos-preview-item]");
    const title = getPreviewTitle(preview);
    button.setAttribute(
      "aria-label",
      window.NaxosText("naxos_ui.preview_action_title", { index: isPlaying ? "pausieren" : "abspielen", title: title })
    );
  }

  function syncPreviewButtons() {
    const currentItem = getCurrentItem();
    const isPlaying = Boolean(currentItem && !audio.paused && !clipEndHandled);
    const progress = Math.min(100, Math.max(0, (displayTime / clipDuration) * 100));

    document.querySelectorAll("[data-naxos-preview-button]").forEach((button) => {
      const preview = button.closest("[data-naxos-preview-item]");
      const isCurrent = Boolean(currentItem) && tracksMatch(getTrackFromPreview(preview), currentItem);
      const buttonIsPlaying = isCurrent && isPlaying;

      preview?.classList.toggle("is-playing", buttonIsPlaying);
      button.setAttribute("aria-pressed", String(buttonIsPlaying));
      updateButtonLabel(button, buttonIsPlaying);

      if (isCurrent) {
        preview?.style.setProperty("--naxos-preview-progress", `${progress}%`);
      } else {
        preview?.style.removeProperty("--naxos-preview-progress");
      }
    });
  }

  function updateMediaSession() {
    if (!("mediaSession" in navigator) || typeof window.MediaMetadata !== "function") {
      return;
    }

    const item = getCurrentItem();

    try {
      navigator.mediaSession.metadata = item
        ? new window.MediaMetadata({
            title: item.title,
            artist: window.NaxosText("naxos_ui.preview_brand"),
            album: item.albumTitle || "",
            artwork: item.imageUrl
              ? [{ src: item.imageUrl, sizes: "160x160" }]
              : [],
          })
        : null;
      navigator.mediaSession.playbackState = audio.paused ? "paused" : "playing";
    } catch (_error) {
      // Media Session support varies by browser.
    }
  }

  function updatePlayerTrack() {
    const item = getCurrentItem();

    if (contextElement) {
      contextElement.textContent =
        item && activeIndex >= 0 ? window.NaxosText("naxos_ui.preview_status", { index: activeIndex + 1, count: queue.length }) : window.NaxosText("naxos_ui.preview");
    }

    if (titleElement) {
      titleElement.textContent = item?.title || window.NaxosText("naxos_ui.no_preview_selected");
    }

    if (previousButton) {
      previousButton.disabled = activeIndex <= 0;
    }

    if (nextButton) {
      nextButton.disabled = activeIndex < 0 || activeIndex >= queue.length - 1;
    }

    document.dispatchEvent(
      new CustomEvent("naxos:player:track-change", {
        detail: { track: item ? { ...item } : null },
      })
    );
  }

  function updateProgress(nextTime) {
    displayTime = Math.min(clipDuration, Math.max(0, Number.isFinite(nextTime) ? nextTime : 0));
    const progress = (displayTime / clipDuration) * 100;

    if (currentTimeElement) {
      currentTimeElement.textContent = formatTime(displayTime);
    }

    if (durationElement) {
      durationElement.textContent = "0:30";
    }

    if (progressInput) {
      progressInput.max = String(clipDuration);
      progressInput.value = String(displayTime);
      progressInput.style.setProperty("--naxos-player-progress", `${progress}%`);
      progressInput.setAttribute("aria-valuetext", window.NaxosText("naxos_ui.playback_time_short", { elapsed: formatTime(displayTime) }));
    }
  }

  function updatePlaybackUI() {
    const item = getCurrentItem();
    const isPlaying = Boolean(item && !audio.paused && !clipEndHandled);

    if (toggleButton) {
      toggleButton.disabled = !item;
      toggleButton.setAttribute("aria-pressed", String(isPlaying));
      toggleButton.setAttribute(
        "aria-label",
        `${isPlaying ? window.NaxosText("naxos_ui.preview_pause") : window.NaxosText("naxos_ui.preview_play")}: ${item?.title || window.NaxosText("naxos_ui.preview")}`
      );
    }

    updatePlayerTrack();
    syncPreviewButtons();
    updateMediaSession();
    document.dispatchEvent(
      new CustomEvent("naxos:player:state-change", {
        detail: {
          isPlaying,
          track: item ? { ...item } : null,
        },
      })
    );
  }

  function syncPlayerLayoutState() {
    const isVisible = !player.hidden;
    root.classList.toggle("naxos-player-open", isVisible);

    if (!isVisible) {
      root.style.removeProperty("--naxos-sticky-player-height");
      document.dispatchEvent(
        new CustomEvent("naxos:player:layout-change", {
          detail: { isOpen: false, height: 0 },
        })
      );
      return;
    }

    const playerHeight = Math.ceil(player.getBoundingClientRect().height);

    if (playerHeight > 0) {
      root.style.setProperty("--naxos-sticky-player-height", `${playerHeight}px`);
      document.dispatchEvent(
        new CustomEvent("naxos:player:layout-change", {
          detail: { isOpen: true, height: playerHeight },
        })
      );
    }
  }

  function showPlayer() {
    player.hidden = false;
    syncPlayerLayoutState();
  }

  function resetAudioSource() {
    resettingAudio = true;
    audio.pause();
    audio.removeAttribute("src");
    audio.load();
    resettingAudio = false;
  }

  function setAudioSource(item, startTime) {
    resettingAudio = true;
    audio.pause();
    audio.src = item.url;
    resumeTime = Math.min(clipDuration, Math.max(0, startTime || 0));
    resettingAudio = false;
  }

  function handlePlaybackError() {
    if (playbackErrorHandled || resettingAudio) {
      return;
    }

    playbackErrorHandled = true;
    const failedTitle = getCurrentItem()?.title || window.NaxosText("naxos_ui.this_preview");
    resetAudioSource();
    updatePlaybackUI();
    setState(window.NaxosText("naxos_ui.preview_error"));
    announce(window.NaxosText("naxos_ui.preview_title_error", { title: failedTitle }));
  }

  function beginPlayback(index, options = {}) {
    const item = queue[index];

    if (!item) {
      return;
    }

    const currentItem = getCurrentItem();
    const isSameTrack =
      options.forceSource !== true &&
      currentItem?.url === item.url &&
      currentItem?.title === item.title;
    const restart = options.restart === true;
    let startTime = restart ? 0 : isSameTrack ? displayTime : 0;

    activeIndex = index;
    clipEndHandled = false;
    playbackErrorHandled = false;
    showPlayer();
    document.dispatchEvent(new CustomEvent("naxos:player:activated"));
    updatePlayerTrack();

    if (!isSameTrack || !audio.hasAttribute("src")) {
      setAudioSource(item, startTime);
    } else if (restart || audio.ended || displayTime >= clipDuration) {
      startTime = 0;
      resumeTime = 0;

      try {
        audio.currentTime = 0;
      } catch (_error) {
        // The loadedmetadata handler applies the position when needed.
      }
    }

    const playAudio = () => {
      if (resumeTime > 0) {
        try {
          audio.currentTime = resumeTime;
        } catch (_error) {
          // The browser will retain its current playable position.
        }
      }

      resumeTime = 0;
      const playAttempt = audio.play();

      if (playAttempt && typeof playAttempt.catch === "function") {
        playAttempt.catch(handlePlaybackError);
      }
    };

    setState(window.NaxosText("naxos_ui.playback_starting"));
    announce(window.NaxosText("naxos_ui.preview_title_playing", { title: item.title }));

    if (audio.readyState >= 1 || resumeTime === 0) {
      playAudio();
    } else {
      audio.addEventListener("loadedmetadata", playAudio, { once: true });
    }

    updateProgress(startTime);
    updatePlaybackUI();
    saveState();
  }

  function pausePlayback() {
    const item = getCurrentItem();

    if (!item) {
      return;
    }

    resettingAudio = true;
    audio.pause();
    resettingAudio = false;
    setState(window.NaxosText("naxos_ui.paused"));
    updatePlaybackUI();
    saveState();
    announce(window.NaxosText("naxos_ui.preview_title_paused", { title: item.title }));
  }

  function finishClip() {
    if (clipEndHandled || activeIndex < 0) {
      return;
    }

    clipEndHandled = true;
    resettingAudio = true;
    audio.pause();
    resettingAudio = false;
    updateProgress(clipDuration);
    updatePlaybackUI();
    setState(activeIndex < queue.length - 1 ? window.NaxosText("naxos_ui.preview_ended") : window.NaxosText("naxos_ui.playlist_ended"));

    const title = getCurrentItem()?.title || window.NaxosText("naxos_ui.preview");
    announce(
      activeIndex < queue.length - 1
        ? window.NaxosText("naxos_ui.preview_title_next", { title: title })
        : window.NaxosText("naxos_ui.preview_title_end", { title: title })
    );
    saveState();
  }

  function stopAndClose(options = {}) {
    const currentItem = getCurrentItem();
    const returnFocusButton = Array.from(document.querySelectorAll("[data-naxos-preview-button]")).find((button) => {
      const preview = button.closest("[data-naxos-preview-item]");
      return currentItem && getPreviewUrl(preview) === currentItem.url && getPreviewTitle(preview) === currentItem.title;
    });

    resetAudioSource();
    queue = [];
    activeIndex = -1;
    displayTime = 0;
    resumeTime = 0;
    clipEndHandled = false;
    updateProgress(0);
    updatePlaybackUI();
    setState(window.NaxosText("naxos_ui.ready"));
    player.hidden = true;
    syncPlayerLayoutState();
    clearSavedState();

    if (options.restoreFocus !== false) {
      if (returnFocusButton && !returnFocusButton.closest("[hidden]")) {
        returnFocusButton.focus({ preventScroll: true });
      } else {
        const mainHeading = document.querySelector("#MainContent h1, #MainContent h2");
        mainHeading?.setAttribute("tabindex", "-1");
        mainHeading?.focus({ preventScroll: true });
      }
    }

    announce(window.NaxosText("naxos_ui.player_closed"));
  }

  function restoreState() {
    if (isRestrictedPlaybackPath(window.location.pathname)) {
      clearSavedState();
      return;
    }

    let storedState;

    try {
      storedState = JSON.parse(window.sessionStorage.getItem(storageKey) || "null");
    } catch (_error) {
      return;
    }

    if (
      !storedState?.visible ||
      !Array.isArray(storedState.queue) ||
      !storedState.queue.length ||
      !Number.isInteger(storedState.activeIndex)
    ) {
      return;
    }

    queue = storedState.queue.filter(
      (item) => item && typeof item.url === "string" && item.url && typeof item.title === "string"
    );
    activeIndex = Math.min(queue.length - 1, Math.max(0, storedState.activeIndex));
    displayTime = Math.min(clipDuration, Math.max(0, Number(storedState.currentTime) || 0));
    resumeTime = displayTime;
    showPlayer();
    updateProgress(displayTime);
    updatePlaybackUI();
    setState(window.NaxosText("naxos_ui.paused_resume"));
  }

  function installMediaSessionHandlers() {
    if (!("mediaSession" in navigator)) {
      return;
    }

    const handlers = {
      nexttrack: () => {
        if (activeIndex < queue.length - 1) beginPlayback(activeIndex + 1, { restart: true });
      },
      pause: pausePlayback,
      play: () => {
        if (activeIndex >= 0) beginPlayback(activeIndex);
      },
      previoustrack: () => {
        if (activeIndex > 0) beginPlayback(activeIndex - 1, { restart: true });
      },
      seekto: (details) => {
        if (Number.isFinite(details.seekTime)) {
          seekTo(details.seekTime);
        }
      },
    };

    Object.entries(handlers).forEach(([action, handler]) => {
      try {
        navigator.mediaSession.setActionHandler(action, handler);
      } catch (_error) {
        // Unsupported actions are ignored.
      }
    });
  }

  function seekTo(requestedTime) {
    if (activeIndex < 0) {
      return;
    }

    const nextTime = Math.min(clipDuration, Math.max(0, Number(requestedTime) || 0));
    clipEndHandled = false;

    if (audio.hasAttribute("src")) {
      const availableDuration =
        Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : clipDuration;

      try {
        audio.currentTime = Math.min(nextTime, availableDuration);
      } catch (_error) {
        resumeTime = nextTime;
      }
    } else {
      resumeTime = nextTime;
    }

    updateProgress(nextTime);

    if (nextTime >= clipDuration) {
      finishClip();
    } else {
      setState(audio.paused ? window.NaxosText("naxos_ui.paused") : window.NaxosText("naxos_ui.playing"));
      updatePlaybackUI();
      saveState();
    }
  }

  function playQueue(nextQueue, startIndex = 0) {
    const playableQueue = Array.isArray(nextQueue)
      ? nextQueue
          .map((item) => ({
            albumTitle: String(item?.albumTitle || ""),
            artist: String(item?.artist || ""),
            composer: String(item?.composer || ""),
            id: String(item?.id || ""),
            imageUrl: String(item?.imageUrl || ""),
            number: String(item?.number || ""),
            productUrl: String(item?.productUrl || ""),
            title: String(item?.title || window.NaxosText("naxos_ui.preview")),
            url: String(item?.url || ""),
          }))
          .filter((item) => item.url)
      : [];

    if (!playableQueue.length) {
      return false;
    }

    queue = playableQueue;
    const safeStartIndex = Math.min(
      playableQueue.length - 1,
      Math.max(0, Number.parseInt(startIndex, 10) || 0)
    );
    beginPlayback(safeStartIndex, { forceSource: true, restart: true });
    return true;
  }

  document.addEventListener("click", (event) => {
    const button =
      event.target instanceof Element ? event.target.closest("[data-naxos-preview-button]") : null;

    if (!button || button.disabled) {
      return;
    }

    const nextQueue = collectQueue(button);
    const nextIndex = findButtonIndex(button, nextQueue);

    if (nextIndex < 0) {
      return;
    }

    event.preventDefault();
    const clickedItem = nextQueue[nextIndex];
    const currentItem = getCurrentItem();
    const isCurrentTrack = tracksMatch(currentItem, clickedItem);

    const queueChanged = !queuesMatch(queue, nextQueue);

    if (queueChanged) {
      queue = nextQueue;
    }

    if (isCurrentTrack && !audio.paused) {
      pausePlayback();
    } else {
      beginPlayback(nextIndex, { forceSource: queueChanged });
    }
  });

  toggleButton?.addEventListener("click", () => {
    if (activeIndex < 0) {
      return;
    }

    if (audio.paused) {
      beginPlayback(activeIndex);
    } else {
      pausePlayback();
    }
  });

  previousButton?.addEventListener("click", () => {
    if (activeIndex > 0) {
      beginPlayback(activeIndex - 1, { restart: true });
    }
  });

  nextButton?.addEventListener("click", () => {
    if (activeIndex >= 0 && activeIndex < queue.length - 1) {
      beginPlayback(activeIndex + 1, { restart: true });
    }
  });

  progressInput?.addEventListener("input", () => {
    seekTo(Number.parseFloat(progressInput.value));
  });

  closeButton?.addEventListener("click", () => {
    stopAndClose();
  });

  player.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      stopAndClose();
    }
  });

  audio.addEventListener("play", () => {
    setState(window.NaxosText("naxos_ui.playing"));
    updatePlaybackUI();
    saveState();
  });

  audio.addEventListener("pause", () => {
    if (!resettingAudio && !clipEndHandled && activeIndex >= 0) {
      setState(window.NaxosText("naxos_ui.paused"));
    }

    updatePlaybackUI();
    saveState();
  });

  audio.addEventListener("loadedmetadata", () => {
    if (resumeTime > 0) {
      try {
        audio.currentTime = resumeTime;
      } catch (_error) {
        // The browser will retain the nearest playable position.
      }
    }
  });

  audio.addEventListener("timeupdate", () => {
    updateProgress(audio.currentTime || 0);
    syncPreviewButtons();

    const currentSecond = Math.floor(displayTime);
    if (currentSecond !== lastSaveSecond) {
      lastSaveSecond = currentSecond;
      saveState();
    }

    if (audio.currentTime >= clipDuration) {
      finishClip();
    }
  });

  audio.addEventListener("ended", finishClip);
  audio.addEventListener("error", handlePlaybackError);

  document.addEventListener("naxos:page:loaded", () => {
    syncPreviewButtons();
    updatePlaybackUI();
  });

  window.addEventListener("pagehide", saveState);
  window.addEventListener("resize", syncPlayerLayoutState);

  if (typeof window.ResizeObserver === "function") {
    const playerResizeObserver = new window.ResizeObserver(syncPlayerLayoutState);
    playerResizeObserver.observe(player);
  }

  window.NaxosStickyPlayer = {
    getCurrentTrack: () => {
      const item = getCurrentItem();
      return item ? { ...item } : null;
    },
    isActive: () => !player.hidden && activeIndex >= 0,
    isPlaying: () => Boolean(getCurrentItem() && !audio.paused && !clipEndHandled),
    pause: pausePlayback,
    playQueue,
    resume: () => {
      if (activeIndex >= 0) beginPlayback(activeIndex);
    },
    stopAndClose,
    sync: syncPreviewButtons,
  };

  installMediaSessionHandlers();
  updateProgress(0);
  updatePlaybackUI();
  restoreState();
})();
